import {
  faFileCircleQuestion,
  faLightbulb,
  faSquareRootVariable,
  faTriangle,
  faUserGroup,
} from '@fortawesome/pro-regular-svg-icons'
import {
  faBolt,
  faCalculator,
  faCalendarWeek,
  faClock,
  faCommentsQuestionCheck,
  faFunction,
  faHeadSideBrain,
  faMessagesQuestion,
  faUserGroup as faUserGroupSolid,
  faUsers,
} from '@fortawesome/pro-solid-svg-icons'
import { DateTime } from 'luxon'

import {
  ALGEBRA2_LEARNING_LABS_LOGARITHMS_OFFERING_SLUG,
  ALGEBRA2_LEARNING_LABS_POLYNOMIAL_ARITHMETIC_OFFERING_SLUG,
  ALGEBRA2_LEARNING_LABS_TRIGONOMETRY_OFFERING_SLUG,
} from '@/queries/constants'
import type {
  ProgramDetails,
  ProgramFactoid,
  ProgramIteration,
  ProgramMarketingConfig,
  ProgramOfferingInstance,
  ProgramOfferingTemplate,
} from '@/client/services/programs/types'
import { getPublicUrl } from '@/client/services/url/index'
import {
  ALGEBRA_2_WORLD_SLUG,
  ALGEBRA2_LEARNING_LABS_WORLD_SLUG,
} from '@/client/services/worlds/constants'

import {
  EARN_VOLUNTEER_HOURS_BULLET_TEXT,
  KA_LEARNING_LABS_PROGRAM_TUTOR_BENEFITS,
  KA_LEARNING_LABS_SIGN_UP_CARD_LEARNER,
  KA_LEARNING_LABS_SIGN_UP_CARD_TUTOR,
  KA_LEARNING_LABS_WHAT_YOULL_DO_BULLET_PROPS_LEARNER,
  KA_LEARNING_LABS_WHAT_YOULL_DO_BULLET_PROPS_TUTOR,
} from '../constants'
import type { Algebra2LearningLabsIterationSlug } from '../types'

const WHAT_YOULL_DO_ICONS_COLOR = 'primary.solid'

export const ALGEBRA2_LEARNING_LABS_POLYNOMIAL_ARITHMETIC_OFFERING_TEMPLATE: ProgramOfferingTemplate =
  {
    offeringSlug: ALGEBRA2_LEARNING_LABS_POLYNOMIAL_ARITHMETIC_OFFERING_SLUG,
    title: 'Polynomial Arithmetic',
    numWeeks: 4,
    sessionFrequency: { times: 2, per: 'week' },
    sessionLength: 75,
    imageUrl: getPublicUrl(
      'algebra2-learning-labs/algebra2-learning-labs-polynomial-arithmetic-v2.svg'
    ),
    learnerDescription: '',
    learnerDescriptionLong: '',
    wizardIcon: faFunction,
    whatYoullDo: {
      bulletsLearner: KA_LEARNING_LABS_WHAT_YOULL_DO_BULLET_PROPS_LEARNER,
      bulletsTutor: KA_LEARNING_LABS_WHAT_YOULL_DO_BULLET_PROPS_TUTOR,
      iconProps: { color: WHAT_YOULL_DO_ICONS_COLOR },
    },
  }

export const ALGEBRA2_LEARNING_LABS_LOGARITHMS_OFFERING_TEMPLATE: ProgramOfferingTemplate = {
  offeringSlug: ALGEBRA2_LEARNING_LABS_LOGARITHMS_OFFERING_SLUG,
  title: 'Logarithms',
  numWeeks: 3,
  sessionFrequency: { times: 2, per: 'week' },
  sessionLength: 75,
  imageUrl: getPublicUrl('algebra2-learning-labs/algebra2-learning-labs-logarithms.svg'),
  // these are intentionally left blank
  learnerDescription: '',
  learnerDescriptionLong: '',
  wizardIcon: faCalculator,
  whatYoullDo: {
    bulletsLearner: KA_LEARNING_LABS_WHAT_YOULL_DO_BULLET_PROPS_LEARNER,
    bulletsTutor: KA_LEARNING_LABS_WHAT_YOULL_DO_BULLET_PROPS_TUTOR,
    iconProps: { color: WHAT_YOULL_DO_ICONS_COLOR },
  },
}

export const ALGEBRA2_LEARNING_LABS_TRIGONOMETRY_OFFERING_TEMPLATE: ProgramOfferingTemplate = {
  offeringSlug: ALGEBRA2_LEARNING_LABS_TRIGONOMETRY_OFFERING_SLUG,
  title: 'Trigonometry',
  numWeeks: 4,
  sessionFrequency: { times: 2, per: 'week' },
  sessionLength: 75,
  imageUrl: getPublicUrl('algebra2-learning-labs/algebra2-learning-labs-trigonometry.svg'),
  // these are intentionally left blank
  learnerDescription: '',
  learnerDescriptionLong: '',
  wizardIcon: faTriangle,
  whatYoullDo: {
    bulletsLearner: KA_LEARNING_LABS_WHAT_YOULL_DO_BULLET_PROPS_LEARNER,
    bulletsTutor: KA_LEARNING_LABS_WHAT_YOULL_DO_BULLET_PROPS_TUTOR,
    iconProps: { color: WHAT_YOULL_DO_ICONS_COLOR },
  },
}

const ALGEBRA2_FINAL_EXAM_PREP_OFFERING_TEMPLATE_PARTIAL: Omit<
  ProgramOfferingTemplate,
  'offeringSlug' | 'templateSlug' | 'title' | 'wizardIcon'
> = {
  sessionLength: 75,
  numWeeks: 4,
  sessionFrequency: { times: 2, per: 'week' },
  imageUrl: getPublicUrl(
    'algebra2-learning-labs/algebra2-learning-labs-polynomial-arithmetic-v2.svg'
  ),
  learnerDescription:
    'Prepare for your Algebra 2 final exam with a mix of 1-on-1 tutoring and small group sessions',
  whatYoullDo: {
    bulletsLearner: [
      {
        key: 'personalized-lesson-plan',
        icon: faLightbulb,
        body: 'Follow a personalized lesson plan to tackle your most important learning needs',
      },
      {
        key: 'review',
        icon: faFileCircleQuestion,
        body: 'Review concepts that we have verified are most likely to be tested in final exams',
      },
      {
        key: 'sessions',
        icon: faUserGroup,
        body: 'Complete seven small group sessions',
      },
    ],
    bulletsTutor: [
      {
        key: 'personalized-teaching-plan',
        icon: faLightbulb,
        body: 'Guide students through a personalized lesson plan that targets their most critical learning needs',
      },
      {
        key: 'prepare',
        icon: faFileCircleQuestion,
        body: 'Teach verified concepts most likely to appear on final exams using our curated curriculum materials',
      },
      {
        key: 'facilitation',
        icon: faUserGroup,
        body: 'Lead two 1-on-1 sessions and 4 small group sessions with engaged learners',
      },
    ],
    iconProps: { color: WHAT_YOULL_DO_ICONS_COLOR },
  },
}

export const SPRING_2025_ALGEBRA2_FINAL_EXAM_PREP_COHORT_TO_OFFERING_NAME: Record<string, string> =
  {
    'cohort-1': 'May 19-23 Final',
    'cohort-2': 'May 26-30 Final',
    'cohort-3': 'Jun 2-6 Final',
    'cohort-4': 'Jun 9-13 Final',
    'cohort-5': 'Jun 16-20 Final',
    'cohort-6': 'Jun 24-27 Final',
  }
export const SPRING_2025_ALGEBRA2_FINAL_EXAM_PREP_COHORT_TO_FINAL_EXAM_DATES: Record<
  string,
  {
    startDate: DateTime
    endDate: DateTime
  }
> = {
  'cohort-1': {
    startDate: DateTime.local(2025, 5, 19, 0, 0, { zone: 'America/New_York' }),
    endDate: DateTime.local(2025, 5, 23, 0, 0, { zone: 'America/New_York' }),
  },
  'cohort-2': {
    startDate: DateTime.local(2025, 5, 26, 0, 0, { zone: 'America/New_York' }),
    endDate: DateTime.local(2025, 5, 30, 0, 0, { zone: 'America/New_York' }),
  },
  'cohort-3': {
    startDate: DateTime.local(2025, 6, 2, 0, 0, { zone: 'America/New_York' }),
    endDate: DateTime.local(2025, 6, 6, 0, 0, { zone: 'America/New_York' }),
  },
  'cohort-4': {
    startDate: DateTime.local(2025, 6, 9, 0, 0, { zone: 'America/New_York' }),
    endDate: DateTime.local(2025, 6, 13, 0, 0, { zone: 'America/New_York' }),
  },
  'cohort-5': {
    startDate: DateTime.local(2025, 6, 16, 0, 0, { zone: 'America/New_York' }),
    endDate: DateTime.local(2025, 6, 20, 0, 0, { zone: 'America/New_York' }),
  },
  'cohort-6': {
    startDate: DateTime.local(2025, 6, 24, 0, 0, { zone: 'America/New_York' }),
    endDate: DateTime.local(2025, 6, 27, 0, 0, { zone: 'America/New_York' }),
  },
}

export const SPRING_2025_ALGEBRA2_FINAL_EXAM_PREP_START_DATES: Record<string, DateTime> = {
  'cohort-1': DateTime.local(2025, 4, 25, 0, 0, { zone: 'America/New_York' }),
  'cohort-2': DateTime.local(2025, 5, 2, 0, 0, { zone: 'America/New_York' }),
  'cohort-3': DateTime.local(2025, 5, 9, 0, 0, { zone: 'America/New_York' }),
  'cohort-4': DateTime.local(2025, 5, 16, 0, 0, { zone: 'America/New_York' }),
  'cohort-5': DateTime.local(2025, 5, 23, 0, 0, { zone: 'America/New_York' }),
  'cohort-6': DateTime.local(2025, 5, 30, 0, 0, { zone: 'America/New_York' }),
}
const SPRING_2025_ALGEBRA2_FINAL_EXAM_PREP_TUTOR_REGISTRATION_START_DATES: Record<
  string,
  DateTime
> = {
  'recruitment-1': DateTime.local(2025, 3, 31, 0, 0, { zone: 'America/New_York' }),
  'recruitment-2': DateTime.local(2025, 4, 14, 0, 0, { zone: 'America/New_York' }),
  'recruitment-3': DateTime.local(2025, 4, 28, 0, 0, { zone: 'America/New_York' }),
}
export const ALGEBRA2_FINAL_EXAM_PREP_SPRING_2025_PREFIX = 'spring-2025-algebra2-final-exam-prep'
const ALGEBRA2_FINAL_EXAM_PREP_OFFERING_TEMPLATES_WITH_DATES_AND_TEMPLATE_SLUGS: Record<
  string,
  ProgramOfferingInstance
> = {
  'cohort-1': {
    offeringTemplate: {
      ...ALGEBRA2_FINAL_EXAM_PREP_OFFERING_TEMPLATE_PARTIAL,
      offeringSlug: `${ALGEBRA2_FINAL_EXAM_PREP_SPRING_2025_PREFIX}-cohort-1`,
      title: SPRING_2025_ALGEBRA2_FINAL_EXAM_PREP_COHORT_TO_OFFERING_NAME['cohort-1'],
      wizardIcon: faSquareRootVariable,
    },
    startDate: SPRING_2025_ALGEBRA2_FINAL_EXAM_PREP_START_DATES['cohort-1'],
    endDate: SPRING_2025_ALGEBRA2_FINAL_EXAM_PREP_START_DATES['cohort-1']
      .plus({
        weeks: 4,
      })
      .minus({ seconds: 1 }),
    seriesTemplateSlug: `${ALGEBRA2_FINAL_EXAM_PREP_SPRING_2025_PREFIX}-cohort-1`,
  },
  'cohort-2': {
    offeringTemplate: {
      ...ALGEBRA2_FINAL_EXAM_PREP_OFFERING_TEMPLATE_PARTIAL,
      offeringSlug: `${ALGEBRA2_FINAL_EXAM_PREP_SPRING_2025_PREFIX}-cohort-2`,
      title: SPRING_2025_ALGEBRA2_FINAL_EXAM_PREP_COHORT_TO_OFFERING_NAME['cohort-2'],
      wizardIcon: faSquareRootVariable,
    },
    startDate: SPRING_2025_ALGEBRA2_FINAL_EXAM_PREP_START_DATES['cohort-2'],
    endDate: SPRING_2025_ALGEBRA2_FINAL_EXAM_PREP_START_DATES['cohort-2']
      .plus({
        weeks: 4,
      })
      .minus({ seconds: 1 }),
    seriesTemplateSlug: `${ALGEBRA2_FINAL_EXAM_PREP_SPRING_2025_PREFIX}-cohort-2`,
  },
  'cohort-3': {
    offeringTemplate: {
      ...ALGEBRA2_FINAL_EXAM_PREP_OFFERING_TEMPLATE_PARTIAL,
      offeringSlug: `${ALGEBRA2_FINAL_EXAM_PREP_SPRING_2025_PREFIX}-cohort-3`,
      title: SPRING_2025_ALGEBRA2_FINAL_EXAM_PREP_COHORT_TO_OFFERING_NAME['cohort-3'],
      wizardIcon: faSquareRootVariable,
    },
    startDate: SPRING_2025_ALGEBRA2_FINAL_EXAM_PREP_START_DATES['cohort-3'],
    endDate: SPRING_2025_ALGEBRA2_FINAL_EXAM_PREP_START_DATES['cohort-3']
      .plus({
        weeks: 4,
      })
      .minus({ seconds: 1 }),
    seriesTemplateSlug: `${ALGEBRA2_FINAL_EXAM_PREP_SPRING_2025_PREFIX}-cohort-3`,
  },
  'cohort-4': {
    offeringTemplate: {
      ...ALGEBRA2_FINAL_EXAM_PREP_OFFERING_TEMPLATE_PARTIAL,
      offeringSlug: `${ALGEBRA2_FINAL_EXAM_PREP_SPRING_2025_PREFIX}-cohort-4`,
      title: SPRING_2025_ALGEBRA2_FINAL_EXAM_PREP_COHORT_TO_OFFERING_NAME['cohort-4'],
      wizardIcon: faSquareRootVariable,
    },
    startDate: SPRING_2025_ALGEBRA2_FINAL_EXAM_PREP_START_DATES['cohort-4'],
    endDate: SPRING_2025_ALGEBRA2_FINAL_EXAM_PREP_START_DATES['cohort-4']
      .plus({
        weeks: 4,
      })
      .minus({ seconds: 1 }),
    seriesTemplateSlug: `${ALGEBRA2_FINAL_EXAM_PREP_SPRING_2025_PREFIX}-cohort-4`,
  },
  'cohort-5': {
    offeringTemplate: {
      ...ALGEBRA2_FINAL_EXAM_PREP_OFFERING_TEMPLATE_PARTIAL,
      offeringSlug: `${ALGEBRA2_FINAL_EXAM_PREP_SPRING_2025_PREFIX}-cohort-5`,
      title: SPRING_2025_ALGEBRA2_FINAL_EXAM_PREP_COHORT_TO_OFFERING_NAME['cohort-5'],
      wizardIcon: faSquareRootVariable,
    },
    startDate: SPRING_2025_ALGEBRA2_FINAL_EXAM_PREP_START_DATES['cohort-5'],
    endDate: SPRING_2025_ALGEBRA2_FINAL_EXAM_PREP_START_DATES['cohort-5']
      .plus({
        weeks: 4,
      })
      .minus({ seconds: 1 }),
    seriesTemplateSlug: `${ALGEBRA2_FINAL_EXAM_PREP_SPRING_2025_PREFIX}-cohort-5`,
  },
  'cohort-6': {
    offeringTemplate: {
      ...ALGEBRA2_FINAL_EXAM_PREP_OFFERING_TEMPLATE_PARTIAL,
      offeringSlug: `${ALGEBRA2_FINAL_EXAM_PREP_SPRING_2025_PREFIX}-cohort-6`,
      title: SPRING_2025_ALGEBRA2_FINAL_EXAM_PREP_COHORT_TO_OFFERING_NAME['cohort-6'],
      wizardIcon: faSquareRootVariable,
    },
    startDate: SPRING_2025_ALGEBRA2_FINAL_EXAM_PREP_START_DATES['cohort-6'],
    endDate: SPRING_2025_ALGEBRA2_FINAL_EXAM_PREP_START_DATES['cohort-6']
      .plus({
        weeks: 4,
      })
      .minus({ seconds: 1 }),
    seriesTemplateSlug: `${ALGEBRA2_FINAL_EXAM_PREP_SPRING_2025_PREFIX}-cohort-6`,
  },
}

export const ALGEBRA2_LEARNING_LABS_ITERATIONS: Record<
  Algebra2LearningLabsIterationSlug,
  ProgramIteration
> = {
  'november-2024': {
    iterationSlug: 'november-2024',
    title: 'November',
    learnerRegistrationOpens: DateTime.local(2024, 10, 28, 0, 0, 0, 0, {
      zone: 'America/New_York',
    }),
    // TODO [confirm with matt]: this doesn't match the programs calendar, but matches the september convention of going until series start
    learnerRegistrationDeadline: DateTime.local(2024, 11, 7, 23, 59, {
      zone: 'America/New_York',
    }),
    tutorRegistrationOpens: DateTime.local(2024, 10, 14, 0, 0, 0, 0, {
      zone: 'America/New_York',
    }),
    tutorRegistrationDeadline: DateTime.local(2024, 10, 28, 23, 59, {
      zone: 'America/New_York',
    }),
    startDate: DateTime.local(2024, 11, 8, 0, 0, { zone: 'America/New_York' }),
    endDate: DateTime.local(2024, 12, 5, 23, 59, {
      zone: 'America/New_York',
    }),
    offerings: [
      {
        offeringTemplate: ALGEBRA2_LEARNING_LABS_POLYNOMIAL_ARITHMETIC_OFFERING_TEMPLATE,
        startDate: DateTime.local(2024, 11, 8, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2024, 12, 5, 0, 0, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'ka-learning-labs-alg2-unit1-2024-11',
      },
      // note that this is the only offering that is 3 weeks long
      {
        offeringTemplate: ALGEBRA2_LEARNING_LABS_LOGARITHMS_OFFERING_TEMPLATE,
        startDate: DateTime.local(2024, 11, 8, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2024, 11, 28, 0, 0, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'ka-learning-labs-alg2-unit8-2024-11',
      },
      {
        offeringTemplate: ALGEBRA2_LEARNING_LABS_TRIGONOMETRY_OFFERING_TEMPLATE,
        startDate: DateTime.local(2024, 11, 8, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2024, 12, 5, 0, 0, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'ka-learning-labs-alg2-unit11-2024-11',
      },
    ],
    recommendedHostingRatios: [
      {
        offeringSlug: ALGEBRA2_LEARNING_LABS_POLYNOMIAL_ARITHMETIC_OFFERING_SLUG,
        ratio: 0.2,
      },
      {
        offeringSlug: ALGEBRA2_LEARNING_LABS_LOGARITHMS_OFFERING_SLUG,
        ratio: 0.4,
      },
      {
        offeringSlug: ALGEBRA2_LEARNING_LABS_TRIGONOMETRY_OFFERING_SLUG,
        ratio: 0.4,
      },
    ],
    tutorPreSeriesSurveyUrl: 'https://form.typeform.com/to/OQ1bMRnT',
    tutorPostSeriesSurveyUrl: 'https://form.typeform.com/to/U47uXzg0',
    learnerPreSeriesSurveyUrl: 'https://form.typeform.com/to/thpeZl38',
    learnerPostSeriesSurveyUrl: 'https://form.typeform.com/to/uVelrRJ3',
  },
  'february-2025': {
    iterationSlug: 'february-2025',
    title: 'February',
    learnerRegistrationOpens: DateTime.local(2025, 1, 23, 0, 0, 0, 0, { zone: 'America/New_York' }),
    learnerRegistrationDeadline: DateTime.local(2025, 2, 6, 23, 59, { zone: 'America/New_York' }),
    tutorRegistrationOpens: DateTime.local(2025, 1, 13, 0, 0, 0, 0, { zone: 'America/New_York' }),
    tutorRegistrationDeadline: DateTime.local(2025, 2, 3, 23, 59, { zone: 'America/New_York' }),
    startDate: DateTime.local(2025, 2, 7, 0, 0, { zone: 'America/New_York' }),
    endDate: DateTime.local(2025, 3, 6, 23, 59, { zone: 'America/New_York' }),
    tutorPreSeriesSurveyUrl: 'https://form.typeform.com/to/tceepEbD',
    tutorPostSeriesSurveyUrl: 'https://form.typeform.com/to/dXzaWj1q',
    learnerPreSeriesSurveyUrl: 'https://form.typeform.com/to/ML9LMaYD',
    learnerPostSeriesSurveyUrl: 'https://form.typeform.com/to/lknwDyLQ',
    offerings: [
      {
        offeringTemplate: ALGEBRA2_LEARNING_LABS_POLYNOMIAL_ARITHMETIC_OFFERING_TEMPLATE,
        startDate: DateTime.local(2025, 2, 7, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2025, 3, 6, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'ka-learning-labs-alg2-unit1-2025-02',
      },
      // note that this is the only offering that is 3 weeks long
      {
        offeringTemplate: ALGEBRA2_LEARNING_LABS_LOGARITHMS_OFFERING_TEMPLATE,
        startDate: DateTime.local(2025, 2, 7, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2025, 2, 27, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'ka-learning-labs-alg2-unit8-2025-02',
      },
      {
        offeringTemplate: ALGEBRA2_LEARNING_LABS_TRIGONOMETRY_OFFERING_TEMPLATE,
        startDate: DateTime.local(2025, 2, 7, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2025, 3, 6, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'ka-learning-labs-alg2-unit11-2025-02',
      },
    ],
    recommendedHostingRatios: [
      {
        offeringSlug: ALGEBRA2_LEARNING_LABS_POLYNOMIAL_ARITHMETIC_OFFERING_SLUG,
        ratio: 0.2,
      },
      {
        offeringSlug: ALGEBRA2_LEARNING_LABS_LOGARITHMS_OFFERING_SLUG,
        ratio: 0.4,
      },
      {
        offeringSlug: ALGEBRA2_LEARNING_LABS_TRIGONOMETRY_OFFERING_SLUG,
        ratio: 0.4,
      },
    ],
  },
  'march-2025': {
    iterationSlug: 'march-2025',
    title: 'March',
    learnerRegistrationOpens: DateTime.local(2025, 2, 20, 0, 0, 0, 0, { zone: 'America/New_York' }),
    learnerRegistrationDeadline: DateTime.local(2025, 3, 6, 23, 59, { zone: 'America/New_York' }),
    tutorRegistrationOpens: DateTime.local(2025, 2, 10, 0, 0, 0, 0, { zone: 'America/New_York' }),
    tutorRegistrationDeadline: DateTime.local(2025, 2, 24, 23, 59, { zone: 'America/New_York' }),
    startDate: DateTime.local(2025, 3, 7, 0, 0, { zone: 'America/New_York' }),
    endDate: DateTime.local(2025, 4, 3, 23, 59, { zone: 'America/New_York' }),
    offerings: [
      {
        offeringTemplate: ALGEBRA2_LEARNING_LABS_POLYNOMIAL_ARITHMETIC_OFFERING_TEMPLATE,
        startDate: DateTime.local(2025, 3, 7, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2025, 4, 3, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'ka-learning-labs-alg2-unit1-2025-03',
      },
      // note that this is the only offering that is 3 weeks long
      {
        offeringTemplate: ALGEBRA2_LEARNING_LABS_LOGARITHMS_OFFERING_TEMPLATE,
        startDate: DateTime.local(2025, 3, 7, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2025, 3, 26, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'ka-learning-labs-alg2-unit8-2025-03',
      },
      {
        offeringTemplate: ALGEBRA2_LEARNING_LABS_TRIGONOMETRY_OFFERING_TEMPLATE,
        startDate: DateTime.local(2025, 3, 7, 0, 0, { zone: 'America/New_York' }),
        endDate: DateTime.local(2025, 4, 3, 23, 59, { zone: 'America/New_York' }),
        seriesTemplateSlug: 'ka-learning-labs-alg2-unit11-2025-03',
      },
    ],
  },
  '2025-spring-recruitment-1': {
    iterationSlug: '2025-spring-recruitment-1',
    title: '2025 Final Exam Prep',
    learnerRegistrationOpens: DateTime.local(2025, 4, 10, 0, 0, 0, 0, { zone: 'America/New_York' }),
    learnerRegistrationDeadline: SPRING_2025_ALGEBRA2_FINAL_EXAM_PREP_START_DATES['cohort-1'].minus(
      { seconds: 1 }
    ),
    tutorRegistrationOpens:
      SPRING_2025_ALGEBRA2_FINAL_EXAM_PREP_TUTOR_REGISTRATION_START_DATES['recruitment-1'],
    tutorRegistrationDeadline: SPRING_2025_ALGEBRA2_FINAL_EXAM_PREP_TUTOR_REGISTRATION_START_DATES[
      'recruitment-2'
    ].minus({ seconds: 1 }),
    startDate: SPRING_2025_ALGEBRA2_FINAL_EXAM_PREP_START_DATES['cohort-1'],
    endDate: SPRING_2025_ALGEBRA2_FINAL_EXAM_PREP_START_DATES['cohort-1'],
    offerings: [
      ALGEBRA2_FINAL_EXAM_PREP_OFFERING_TEMPLATES_WITH_DATES_AND_TEMPLATE_SLUGS['cohort-1'],
      ALGEBRA2_FINAL_EXAM_PREP_OFFERING_TEMPLATES_WITH_DATES_AND_TEMPLATE_SLUGS['cohort-2'],
      ALGEBRA2_FINAL_EXAM_PREP_OFFERING_TEMPLATES_WITH_DATES_AND_TEMPLATE_SLUGS['cohort-3'],
      ALGEBRA2_FINAL_EXAM_PREP_OFFERING_TEMPLATES_WITH_DATES_AND_TEMPLATE_SLUGS['cohort-4'],
      ALGEBRA2_FINAL_EXAM_PREP_OFFERING_TEMPLATES_WITH_DATES_AND_TEMPLATE_SLUGS['cohort-5'],
      ALGEBRA2_FINAL_EXAM_PREP_OFFERING_TEMPLATES_WITH_DATES_AND_TEMPLATE_SLUGS['cohort-6'],
    ],
    recommendedHostingRatios: [
      {
        offeringSlug: `${ALGEBRA2_FINAL_EXAM_PREP_SPRING_2025_PREFIX}-cohort-1`,
        ratio: 0.3,
      },
      {
        offeringSlug: `${ALGEBRA2_FINAL_EXAM_PREP_SPRING_2025_PREFIX}-cohort-2`,
        ratio: 0.3,
      },
      {
        offeringSlug: `${ALGEBRA2_FINAL_EXAM_PREP_SPRING_2025_PREFIX}-cohort-3`,
        ratio: 0.1,
      },
      {
        offeringSlug: `${ALGEBRA2_FINAL_EXAM_PREP_SPRING_2025_PREFIX}-cohort-4`,
        ratio: 0.1,
      },
      {
        offeringSlug: `${ALGEBRA2_FINAL_EXAM_PREP_SPRING_2025_PREFIX}-cohort-5`,
        ratio: 0.1,
      },
      {
        offeringSlug: `${ALGEBRA2_FINAL_EXAM_PREP_SPRING_2025_PREFIX}-cohort-6`,
        ratio: 0.1,
      },
    ],
    tutorPreSeriesSurveyUrl: 'https://form.typeform.com/to/NKC6HUTM',
    tutorPostSeriesSurveyUrl: 'https://form.typeform.com/to/ZOpatxSI',
    learnerPreSeriesSurveyUrl: 'https://form.typeform.com/to/mnXP8bBe',
    learnerPostSeriesSurveyUrl: 'https://form.typeform.com/to/DvPSgUXN',
  },
  '2025-spring-recruitment-2': {
    iterationSlug: '2025-spring-recruitment-2',
    title: '2025 Final Exam Prep',
    learnerRegistrationOpens: SPRING_2025_ALGEBRA2_FINAL_EXAM_PREP_START_DATES['cohort-1'],
    learnerRegistrationDeadline: SPRING_2025_ALGEBRA2_FINAL_EXAM_PREP_START_DATES['cohort-4'].minus(
      { seconds: 1 }
    ),
    tutorRegistrationOpens:
      SPRING_2025_ALGEBRA2_FINAL_EXAM_PREP_TUTOR_REGISTRATION_START_DATES['recruitment-2'],
    tutorRegistrationDeadline: SPRING_2025_ALGEBRA2_FINAL_EXAM_PREP_TUTOR_REGISTRATION_START_DATES[
      'recruitment-3'
    ].minus({ seconds: 1 }),
    startDate: SPRING_2025_ALGEBRA2_FINAL_EXAM_PREP_START_DATES['cohort-3'],
    endDate: SPRING_2025_ALGEBRA2_FINAL_EXAM_PREP_START_DATES['cohort-4'].minus({ seconds: 1 }),
    offerings: [
      ALGEBRA2_FINAL_EXAM_PREP_OFFERING_TEMPLATES_WITH_DATES_AND_TEMPLATE_SLUGS['cohort-4'],
      ALGEBRA2_FINAL_EXAM_PREP_OFFERING_TEMPLATES_WITH_DATES_AND_TEMPLATE_SLUGS['cohort-5'],
      ALGEBRA2_FINAL_EXAM_PREP_OFFERING_TEMPLATES_WITH_DATES_AND_TEMPLATE_SLUGS['cohort-6'],
    ],
    recommendedHostingRatios: [
      {
        offeringSlug: `${ALGEBRA2_FINAL_EXAM_PREP_SPRING_2025_PREFIX}-cohort-4`,
        ratio: 0.54,
      },
      {
        offeringSlug: `${ALGEBRA2_FINAL_EXAM_PREP_SPRING_2025_PREFIX}-cohort-5`,
        ratio: 0.23,
      },
      {
        offeringSlug: `${ALGEBRA2_FINAL_EXAM_PREP_SPRING_2025_PREFIX}-cohort-6`,
        ratio: 0.23,
      },
    ],
  },
  '2025-spring-recruitment-3': {
    iterationSlug: '2025-spring-recruitment-3',
    title: '2025 Final Exam Prep',
    learnerRegistrationOpens: SPRING_2025_ALGEBRA2_FINAL_EXAM_PREP_START_DATES['cohort-4'],
    learnerRegistrationDeadline: SPRING_2025_ALGEBRA2_FINAL_EXAM_PREP_START_DATES['cohort-6'].minus(
      { seconds: 1 }
    ),
    tutorRegistrationOpens:
      SPRING_2025_ALGEBRA2_FINAL_EXAM_PREP_TUTOR_REGISTRATION_START_DATES['recruitment-3'],
    tutorRegistrationDeadline: DateTime.local(2025, 5, 11, 23, 59, { zone: 'America/New_York' }),
    startDate: SPRING_2025_ALGEBRA2_FINAL_EXAM_PREP_START_DATES['cohort-5'],
    endDate: SPRING_2025_ALGEBRA2_FINAL_EXAM_PREP_START_DATES['cohort-6'],
    offerings: [
      // NOTE: we did have cohort 5 in here as well but we didn't want to keep showing it in the wizard so removing it
      // now that the start date for cohort 5 has passed.
      ALGEBRA2_FINAL_EXAM_PREP_OFFERING_TEMPLATES_WITH_DATES_AND_TEMPLATE_SLUGS['cohort-6'],
    ],
    recommendedHostingRatios: [
      {
        offeringSlug: `${ALGEBRA2_FINAL_EXAM_PREP_SPRING_2025_PREFIX}-cohort-5`,
        ratio: 0.5,
      },
      {
        offeringSlug: `${ALGEBRA2_FINAL_EXAM_PREP_SPRING_2025_PREFIX}-cohort-6`,
        ratio: 0.5,
      },
    ],
  },
}

export const ALGEBRA2_LEARNING_LABS_PROGRAM_FACTOIDS: ProgramFactoid[] = [
  {
    key: 'program-duration',
    text: '4 weeks',
    subtext: '7 total sessions',
    iconProps: { size: 'md', color: 'primary.solid', faIcon: faCalendarWeek },
  },
  {
    key: 'session-duration',
    text: '75 minutes',
    subtext: 'Per session',
    iconProps: { size: 'md', color: 'primary.solid', faIcon: faClock },
  },
  {
    key: 'max-session-capacity',
    text: '10 learners',
    subtext: 'Max. class size',
    iconProps: { size: 'md', color: 'primary.solid', faIcon: faUsers },
  },
]

export const ALGEBRA2_LEARNING_LABS_MARKETING_CONFIG: ProgramMarketingConfig = {
  header: {
    learnerText: 'Ace your final exam with personalized tutoring.',
    learnerSubtext: 'Stuck on a concept? Lean on a peer tutor to progress your learning',
    tutorText: 'Help students ace their final exam with personalized tutoring.',
    backgroundImageUrl: 'algebra2-learning-labs/algebra2-learning-labs-header-background.svg',
    backgroundColor: 'primary.solid',
    factoids: ALGEBRA2_LEARNING_LABS_PROGRAM_FACTOIDS,
    partnerships: ['KhanAcademy'],
  },
  whatYoullDo: {
    iconProps: { color: WHAT_YOULL_DO_ICONS_COLOR },
    bulletsLearner: [
      {
        key: 'review-reinforce',
        icon: faUserGroupSolid,
        body: 'Work through a custom study plan based on your learning needs',
      },
      {
        key: 'personalized-support',
        icon: faCommentsQuestionCheck,
        body: 'Study with other students in seven small group sessions',
      },
      {
        key: 'stay-motivated',
        icon: faBolt,
        body: 'Review the most important test prep materials verified by teachers',
      },
    ],
    bulletsTutor: [
      {
        key: 'empower-students',
        icon: faUserGroup,
        body: 'Empower your students in a 4-week course to prepare for their final exam',
      },
      {
        key: 'personalized-tutoring',
        icon: faCommentsQuestionCheck,
        body: 'Support learners through a mix of 1:1 and small group tutoring',
      },
      {
        key: 'earn-volunteer-hours',
        icon: faBolt,
        body: EARN_VOLUNTEER_HOURS_BULLET_TEXT,
      },
    ],
  },
  tutorSignUpCard: KA_LEARNING_LABS_SIGN_UP_CARD_TUTOR,
  learnerSignUpCard: KA_LEARNING_LABS_SIGN_UP_CARD_LEARNER,
  tutorBenefits: KA_LEARNING_LABS_PROGRAM_TUTOR_BENEFITS,
  // TODO: change back to KA_LEARNING_LABS_PROGRAM_LEARNER_BENEFITS after the KA Algebra 2 Finals program is over
  learnerBenefits: [
    {
      id: 'ace-final-exam',
      text: 'Ace your final exam with personalized tutoring',
      faIcon: faUserGroup,
      iconProps: { color: 'cyan.solid', size: 'lg' },
    },
    {
      id: 'custom-study-plan',
      text: 'Work through a custom study plan based on your learning needs',
      faIcon: faHeadSideBrain,
      iconProps: { color: 'yellow.solid', size: 'lg' },
    },
    {
      id: 'verified-test-prep-materials',
      text: 'Review the most important test prep material verified by teachers',
      faIcon: faMessagesQuestion,
      iconProps: { color: 'pink.solid', size: 'lg' },
    },
    {
      id: 'small-group-tutoring',
      text: "Learn in small group sessions tailored to your group's specific needs",
      faIcon: faBolt,
      iconProps: { color: 'success.solid', size: 'lg' },
    },
  ],
  programFactoids: ALGEBRA2_LEARNING_LABS_PROGRAM_FACTOIDS,
}

export const ALGEBRA2_LEARNING_LABS_PROGRAM_DETAILS: ProgramDetails = {
  slug: 'algebra2-learning-labs',
  title: 'Algebra 2 Learning Labs',
  description: 'Ace your final exam with personalized tutoring.',
  worldSlug: ALGEBRA2_LEARNING_LABS_WORLD_SLUG,
  fallbackWorldSlug: ALGEBRA_2_WORLD_SLUG,
  programType: 'learning lab',
  learnerWaitlistName: 'learning-labs-algebra2-learner',
  tutorWaitlistName: 'learning-labs-algebra2-tutor',
}

export const FINALS_PREP_PRE_SURVEY_ASSESSMENT_ID = 5331
export const FINALS_PREP_SKILLS_ASSESSMENT_ID = 5560

export const TEMPLATE_SLUGS_FOR_SKILLS_ASSESSMENT_FORMAT = [
  'spring-2025-algebra2-final-exam-prep-cohort-4',
  'spring-2025-algebra2-final-exam-prep-cohort-5',
  'spring-2025-algebra2-final-exam-prep-cohort-6',
]
